import React, { useRef } from "react";
import { motion, useScroll, useSpring } from "framer-motion";
import { FiMapPin } from "react-icons/fi";
import { workExperience } from "../../data/resume";
import { fadeUp, staggerChildren, viewportOnce } from "../../utils/motionPresets";
import "./experience.css";

// Wrap percentages so measurable wins stand out.
const emphasize = (text) =>
  text.split(/(\d+%)/).map((part, i) =>
    /\d+%/.test(part) ? (
      <mark key={i} className="metric">
        {part}
      </mark>
    ) : (
      part
    )
  );

const Experience = () => {
  const listRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: listRef, offset: ["start 70%", "end 60%"] });
  const scaleY = useSpring(scrollYProgress, { stiffness: 120, damping: 30 });

  return (
    <section className="section" id="experience">
      <div className="container experience">
        <motion.aside
          className="experience__aside"
          variants={staggerChildren}
          initial="hidden"
          whileInView="visible"
          viewport={viewportOnce}
        >
          <motion.span className="eyebrow" variants={fadeUp}>
            <span className="eyebrow__index">04</span>
            Experience
          </motion.span>
          <motion.h2 className="section-title" variants={fadeUp}>
            Where I've
            <br />
            <span className="serif">made</span> impact.
          </motion.h2>
          <motion.p className="experience__lede" variants={fadeUp}>
            From agency delivery to security-grade enterprise platforms — a track record of shipping
            under real constraints.
          </motion.p>
        </motion.aside>

        <div className="timeline" ref={listRef}>
          <div className="timeline__rail" aria-hidden="true">
            <motion.div className="timeline__progress" style={{ scaleY }} />
          </div>

          {workExperience.map((job) => (
            <motion.article
              key={`${job.company}-${job.period}`}
              className="timeline__item"
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.3 }}
              transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
            >
              <span className={`timeline__node ${job.current ? "is-current" : ""}`} aria-hidden="true" />
              <header className="timeline__head">
                <div className="timeline__period mono">
                  {job.period}
                  {job.current && <span className="timeline__now">Now</span>}
                </div>
                <h3 className="timeline__role">
                  {job.role} <span className="timeline__at">@</span> {job.company}
                </h3>
                {job.location && (
                  <span className="timeline__location">
                    <FiMapPin /> {job.location}
                  </span>
                )}
              </header>
              <ul className="timeline__list">
                {job.achievements.map((item) => (
                  <li key={item}>{emphasize(item)}</li>
                ))}
              </ul>
            </motion.article>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Experience;
